import React, { useEffect } from 'react'
import {
  Typography,
  Card,
  Grid,
  MenuItem,
  Select,
  CircularProgress,
  OutlinedInput,
  FormControl,
  InputLabel,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import ImageWithFallback from 'app/components/ImageWithFallback'
import NotificationTypePreview from 'app/components/notificationBadge/NotificationTypePreview'
import NotificationImportantPreview from 'app/components/notificationBadge/NotificationImportantPreview'
import NotificationPreviewActions from 'app/main/messages/notificationTimeline/notification-preview/NotificationPreviewActions'
import { removeDoNotTranslateStyle } from 'commons/form'
import RichTextDisplay from 'app/components/richTextEditor/RichTextDisplay'
import assetConfigs from 'app/configs/assetConfigs'
import { usePlace } from 'commons/usePlace'
import clsx from 'clsx'
import Topic from 'app/components/Topic'
import {
  prop,
  compose,
  join,
  ifElse,
  view,
  lensPath,
  isEmpty,
  isNil,
  always,
  map,
  propEq,
  path,
  both,
  complement,
  propSatisfies,
  any,
} from 'ramda'
import Assets from 'app/configs/assetConfigs'
import { FormattedMessage } from 'react-intl'
import useMessageExpiration from 'commons/useMessageExpiration'
import { FuseAnimateGroup } from '@fuse'
import { isEmptyOrNil } from 'libs/empty'
import Loader from 'app/components/Loader'
import { useTranslationPreviewLanguage } from './useTranslationPreviewLanguage'
import NotificationEventDate from 'app/main/messages/notificationTimeline/notification-preview/NotificationEventDate'
import NotificationEventPlace from 'app/main/messages/notificationTimeline/notification-preview/NotificationEventPlace'
import NotificationEventAction from 'app/main/messages/notificationTimeline/notification-preview/NotificationEventAction'
import { LanguageSubscriberCount } from 'app/components/selectOptional'
import ErrorTranslationMessage from 'app/components/ErrorTranslationMessage'
import moment from 'moment-timezone'
import ContainedButton from 'app/componentV4/Button/ContainedButton'
import { VOTE } from 'app/constants/button'
import Radio from '@material-ui/core/Radio'
import RadioGroup from '@material-ui/core/RadioGroup'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import apiConfig from 'app/configs/apiConfigs'
import { TYPE_GENERAL, TYPE_EVENT, TYPE_POLL } from 'app/constants/messageType'
import { htmlToPlainText } from 'libs/text'

export const viewLogo = view(lensPath(['info', 'logo']))
export const viewName = view(lensPath(['name']))

const useStyles = makeStyles((theme) => ({
  rootBody: {
    minHeight: '100px',
  },
  cardEmptyPreview: {
    backgroundColor: '#FAFDFF',
  },
  previewLabel: {
    color: '#C4C4C4',
  },
  previewStyle: {
    borderRadius: 0,
    margin: '1rem',
  },
  placeName: { color: theme.palette.secondary.main },
  placeLogoImage: {
    objectFit: 'cover',
    objectPosition: 'center',
    overflow: 'hidden',
    borderRadius: '50%',
  },
  directionRtl: {
    direction: 'rtl',
  },
  textColorBlur: {
    fontSize: '12px',
    fontWeight: 'bold',
    fontStretch: 'normal',
    fontStyle: 'normal',
    color: '#247bcd',
  },
  dotColor: {
    color: '#00314b',
  },
  voucherTicket: {
    position: 'relative',
    width: '100%',
  },
  voucherTicketText: {
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    top: '30%',
    left: '10%',
  },
  ticketTitle: {
    fontStyle: 'italic',
    fontSize: '14px',
    display: 'block',
    lineHeight: '1.14',
  },
  ticketSubtitle: {
    fontSize: '18px',
    display: 'block',
    lineHeight: '1.14',
  },
  title_category_1: {
    color: '#FFF',
  },
  subtitle_category_1: {
    color: '#006296',
  },
  title_category_2: {
    color: '#444',
  },
  subtitle_category_2: {
    color: '#950',
  },
  title_category_3: {
    color: '#444',
  },
  subtitle_category_3: {
    color: '#444',
  },
  title_category_4: {
    color: '#FF9',
  },
  subtitle_category_4: {
    color: '#FFF',
  },
  title_category_5: {
    color: '#444',
  },
  subtitle_category_5: {
    color: '#F79',
  },
  labelSelected: {
    transform: 'translate(18px, 18px) scale(1) !important',
  },
  languageSelected: {
    '& .MuiSelect-select:focus': {
      backgroundColor: 'unset !important',
    },
  },
  formLabel: {
    '& .MuiFormControlLabel-label': {
      width: '100%',
      color: '#000000 !important',
    },
  },
}))

const isNotNilOrEmpty = both(complement(isNil), complement(isEmpty))
const hasValueOption = any(propSatisfies(isNotNilOrEmpty, 'title'))

const PreviewMessage = (props) => {
  const { notification } = props
  const classes = useStyles()
  const place = usePlace()
  const banners = assetConfigs.dealAndPromotion.banners
  const placeLogo = viewLogo(place)
  const placeName = viewName(place)
  const placeTopics = prop('topics', notification)
  const messageExpiration = useMessageExpiration(notification)
  const isMessageExpiration = messageExpiration.hasExpiration
  const isPoll = propEq('type', TYPE_POLL, notification)
  const isGeneral = propEq('type', TYPE_GENERAL, notification)
  const isEvent = propEq('type', TYPE_EVENT, notification)
  const title = prop('title', notification)
  const body = prop('body', notification)

  const {
    state,
    apiFetchersLanguagesCallbackCompose,
    updateOriginalText,
    updateLanguageSelected,
    translateText,
    onCloseLanguageSelected,
    clearLanguageSelected,
    onOpenSelected,
  } = useTranslationPreviewLanguage()

  const subGroupIds = notification.recipients.subGroups
  const subscriberIds = notification.recipients.subscribers.map(
    (item) => item.id,
  )

  const topicIds = compose(
    join(','),
    ifElse(isNil, always([]), map(prop('id'))),
    prop('topics'),
  )(notification)

  const channelIds = compose(
    ifElse(isNil, always(''), join(',')),
    path(['channels', 'channelIds']),
  )(notification)

  const isShowTranslator =
    (!isEmpty(subGroupIds) || !isEmpty(subscriberIds)) &&
    !isEmpty(channelIds) &&
    !isEmptyOrNil(htmlToPlainText(title)) &&
    !isEmptyOrNil(htmlToPlainText(body))

  const handleOriginalText = () => {
    const topics = placeTopics.map((item) => item.name)
    updateOriginalText({ title, body, topics })
  }

  const handleTranslationText = () => {
    if (title && body && !isNil(state.languageId)) {
      const topics = placeTopics.map((item) => item.name)
      translateText({
        languageId: state.languageId,
        title,
        body,
        translates: state.translates,
        topicIds,
        topics,
      })
    }
  }

  const fetchLanguageOrClear = () => {
    if (
      channelIds &&
      (subGroupIds || subscriberIds) &&
      isEmpty(state.languages)
    ) {
      apiFetchersLanguagesCallbackCompose({
        subGroupIds: !isEmpty(subGroupIds) ? subGroupIds.join(',') : '',
        subscriberIds: !isEmpty(subscriberIds) ? subscriberIds.join(',') : '',
        topicIds,
        channelIds,
        composeTitle: title,
        composeBody: body,
      })
    } else {
      onOpenSelected()
    }
  }

  const getDateLeftOfPoll = (date, timezone) => {
    const now = moment.tz(timezone)
    const targetDate = moment.tz(date, timezone)
    const minutesLeft = targetDate.diff(now, 'minutes')
    const daysLeft = Math.floor(minutesLeft / 1440)
    const hoursLeft = Math.floor((minutesLeft % 1440) / 60)
    let message = ''
    if (daysLeft > 0) {
      message = `${daysLeft} days`
    } else {
      message = `${hoursLeft} hours`
    }

    return (message += ' left')
  }

  const showOptionPoll = (options) => {
    return options
      .filter((item) => isNotNilOrEmpty(item.title))
      .map((item, index) => (
        <RadioGroup className="w-full" key={`option-${index + 1}`}>
          <FormControlLabel
            value={item.title}
            className={classes.formLabel}
            control={<Radio disabled />}
            label={
              <div className="flex justify-between w-full text-blue-500">
                <Typography className="text-blue-500 my-auto">
                  {item.title}
                </Typography>
                <div className="p-2 item-center flex">
                  {isNotNilOrEmpty(item.imagePath) ? (
                    <img
                      src={`${apiConfig.cndUrl}/${item.imagePath}`}
                      alt={item.title}
                      className="w-44 h-44 rounded-8"
                    />
                  ) : (
                    <div className="w-44 h-44 rounded-8 bg-primary-350"></div>
                  )}
                </div>
              </div>
            }
          />
        </RadioGroup>
      ))
  }

  useEffect(handleOriginalText, [
    title,
    body,
    placeTopics.length,
    subGroupIds.length,
    subscriberIds.length,
    channelIds.length,
  ])

  useEffect(handleTranslationText, [state.languageId])

  useEffect(() => {
    clearLanguageSelected()
  }, [
    subGroupIds.length,
    subscriberIds.length,
    channelIds,
    topicIds,
    title,
    body,
  ])

  return (
    <div className="text-blue-950">
      <Typography variant="h1" className="font-bold text-15 mb-4">
        <FormattedMessage id="translationPreview.initialNoti.title" />
      </Typography>
      <div className="mt-5">
        {!isPoll && isShowTranslator && (
          <div className="py-5">
            <label className="text-primary-300 text-14">
              <FormattedMessage id="translationPreview.translate.title" />
            </label>
            <FormControl className="w-full mt-4 bg-white">
              {!state.languageId && (
                <InputLabel
                  htmlFor="languages"
                  className={clsx('m-auto', classes.labelSelected)}
                >
                  <FormattedMessage id="translationPreview.translationSelectLabel" />
                </InputLabel>
              )}

              <Select
                name="languages"
                id="languages"
                open={state.openLanguageSelector}
                className={clsx('w-full h-48', classes.languageSelected)}
                value={state.languageId || ''}
                onChange={updateLanguageSelected}
                onOpen={() => {
                  fetchLanguageOrClear()
                }}
                onClose={onCloseLanguageSelected}
                input={<OutlinedInput />}
              >
                {state.isLoading ? (
                  <MenuItem key="loading" value="" className="text-center">
                    ... Loading
                  </MenuItem>
                ) : (
                  state.languages.map((language) => (
                    <MenuItem key={language.id} value={language.id}>
                      <LanguageSubscriberCount
                        name={language.name}
                        nativeName={language.nativeName}
                        count={language.count}
                      />
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </div>
        )}
        <Card className="border border-gray-200 shadow-0">
          <Loader
            isLoading={state.isLoading || state.isTranslating}
            placeholder={
              <FuseAnimateGroup
                enter={{ animation: 'transition.fadeIn' }}
                leave={{ animation: 'transition.fadeOut' }}
              >
                <div className="flex justify-center py-64">
                  <CircularProgress color="secondary" />
                </div>
              </FuseAnimateGroup>
            }
          >
            {isNil(state.error) ? (
              <div className="flex-col min-w-xs">
                <div className="w-full inline-flex py-4">
                  <div className="flex w-1/4 max-w-64 max-h-64 ltr:pl-16 rtl:pr-16 p-8 items-center">
                    <ImageWithFallback
                      className={classes.placeLogoImage}
                      alt="logo"
                      src={placeLogo}
                      fallbackSrc={Assets.defaultPlaceImage}
                    />
                  </div>
                  <div className="flex-shrink flex-col w-3/4 justify-center py-12 ltr:pr-8 rtl:pl-8 ltr:pl-4 rtl:pr-4">
                    <Typography className={clsx(classes.placeName, 'truncate')}>
                      <b>{placeName}</b>
                    </Typography>
                    <Typography>
                      <FormattedMessage id="notificationPreview.placeholderTime" />
                    </Typography>
                    <div className="flex mt-2">
                      <NotificationTypePreview
                        className="ltr:mr-6 rtl:ml-6"
                        type={notification.type}
                      />
                      <NotificationImportantPreview
                        priority={notification.priority}
                        className="ltr:ml-8 rtl:mr-8"
                      />
                    </div>
                  </div>
                </div>
                <div className="h-full">
                  <div>
                    {isMessageExpiration && (
                      <div className="w-full my-8 border-t-1 border-b-1 border-gray-500">
                        <div className="w-full px-16 py-16 flex items-center">
                          <svg
                            className={clsx(
                              classes.expirationIcon,
                              'h-16 w-16 fill-current',
                            )}
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                          >
                            <path fill="none" d="M0 0h24v24H0z" />
                            <path d="M15 2h-3.5l-1-1h-5l-1 1H1v2h14zM16 9c-.7 0-1.37.1-2 .29V5H2v12c0 1.1.9 2 2 2h5.68A6.999 6.999 0 0 0 23 16c0-3.87-3.13-7-7-7zm0 12c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z" />
                            <path d="M16.5 12H15v5l3.6 2.1.8-1.2-2.9-1.7z" />
                          </svg>
                          <span
                            className={clsx(
                              classes.expirationDate,
                              'ml-8 text-xs',
                            )}
                          >
                            {messageExpiration.expirationDatePreview}
                          </span>
                        </div>
                      </div>
                    )}
                    {isGeneral && (
                      <>
                        <div
                          dir="auto"
                          className="w-full pb-8 px-10 break-words font-bold"
                        >
                          <RichTextDisplay text={state.textDisplay.title} />
                        </div>
                      </>
                    )}
                    {isEvent && (
                      <>
                        <NotificationEventDate
                          startDateTime={path(
                            ['event', 'startDateTime'],
                            notification,
                          )}
                          startTimezone={path(
                            ['event', 'startTimezone'],
                            notification,
                          )}
                          endDateTime={path(
                            ['event', 'endDateTime'],
                            notification,
                          )}
                          endTimezone={path(
                            ['event', 'endTimezone'],
                            notification,
                          )}
                        />
                        <NotificationEventPlace
                          title={
                            <RichTextDisplay text={state.textDisplay.title} />
                          }
                          placeName={path(['event', 'location'], notification)}
                        />
                        <NotificationEventAction readonly={true} />
                      </>
                    )}
                    {(isGeneral || isEvent) && (
                      <div
                        dir="auto"
                        // style={{ minHeight: '300px' }}
                        className={clsx('w-full pb-8 px-10 ', classes.rootBody)}
                      >
                        <div className="break-words pt-8">
                          <RichTextDisplay
                            text={
                              state.textDisplay.body
                                ? removeDoNotTranslateStyle(
                                    state.textDisplay.body,
                                  )
                                : ''
                            }
                          />
                        </div>
                      </div>
                    )}
                    {isPoll && (
                      <>
                        <div
                          dir="auto"
                          // style={{ minHeight: '430px' }}
                          className="w-full pb-8 px-10 "
                        >
                          <div className="font-bold break-words">
                            {notification.question}
                          </div>
                          {isNotNilOrEmpty(notification.question) &&
                            (isNotNilOrEmpty(notification.pollEndDateTime) ? (
                              <div className="text-12 text-gray-400">
                                {getDateLeftOfPoll(
                                  notification.pollEndDateTime,
                                  notification.pollTimezone,
                                )}
                              </div>
                            ) : (
                              <div className="text-12 text-gray-400">
                                <FormattedMessage id="label.open" />
                              </div>
                            ))}
                        </div>
                        {hasValueOption(notification.options) && (
                          <div dir="auto" className="w-full pb-8 px-10 ">
                            <div className="break-words w-full">
                              {showOptionPoll(notification.options)}
                            </div>
                            <div className="break-words flex justify-center w-full my-5">
                              <ContainedButton
                                color={VOTE}
                                disabled={true}
                                type="submit"
                              >
                                <div className="flex ">
                                  <span>Vote</span>
                                </div>
                              </ContainedButton>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    {prop('voucher', notification) && (
                      <div className="relative w-full my-20">
                        <ImageWithFallback
                          alt="ticket"
                          src={
                            banners[
                              `category_${path(
                                ['voucher', 'category'],
                                notification,
                              )}`
                            ]
                          }
                          fallbackSrc={Assets.dealAndPromotion.voucherTicket}
                        />
                        <div className={classes.voucherTicketText}>
                          <Typography
                            variant="button"
                            className={clsx(
                              classes.ticketTitle,
                              classes[
                                `title_category_${path(
                                  ['voucher', 'category'],
                                  notification,
                                )}`
                              ],
                            )}
                          >
                            <FormattedMessage id="createNotification.preview.dialog.voucher.ticket.title" />
                          </Typography>
                          <Typography
                            variant="button"
                            className={clsx(
                              classes.ticketSubtitle,
                              classes[
                                `subtitle_category_${path(
                                  ['voucher', 'category'],
                                  notification,
                                )}`
                              ],
                            )}
                          >
                            <FormattedMessage id="createNotification.preview.dialog.voucher.ticket.subtitle" />
                          </Typography>
                        </div>
                      </div>
                    )}
                    {!isEmptyOrNil(state.topicsDisplay) && (
                      <div dir="auto">
                        <Grid container className="w-full pb-8 px-10">
                          {state.topicsDisplay.map((topic, index) => {
                            return (
                              <div className="mr-2" key={index + 1}>
                                <Topic id={index + 1} name={topic} />
                              </div>
                            )
                          })}
                        </Grid>
                      </div>
                    )}
                  </div>
                  <div className="flex items-end">
                    <NotificationPreviewActions notification={notification} />
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex-col min-w-xs px-10 pb-10">
                <ErrorTranslationMessage />
              </div>
            )}
          </Loader>
        </Card>
      </div>
    </div>
  )
}

export default PreviewMessage
